import React, { useEffect, useMemo, useRef } from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useTopicGroupInclusions } from './hooks/useTopicGroupInclusions';
import { SlPaginator } from 'src/components/BeaconV2Components/SlPaginator';
import { TopicCardRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/types';
import { TopicGroupCard } from './TopicGroupCard';
import { NoDataPlaceHolderTableRow } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { SlClickThruAction } from 'src/components/BeaconV2Components/SlClickThruAction';
import { useQueryParamValue } from 'src/utils/Hooks';
import { SlTripleFolderIcon } from 'src/components/SvgIcons';
import GenericCardLoading from 'src/components/BeaconV2Components/GenericCardLoading/GenericCardLoading';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { TopicGroup, TopicWithOwner } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import { isCustomTopicOrTopicGroup } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { useEditTopicGroupContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/TopicGroupInclusions/TopicGroupInclusionProvider';

function TopicGroupGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '14px'
      }}
    >
      {children}
    </div>
  );
}

const ITEMS_PER_PAGE = 20;

/**
 * Fetches and displays a grid of traffic group cards
 */
export function TopicGroupInclusions() {
  const { isLoading, topicGroups } = useTopicGroupInclusions();

  const anchor = useQueryParamValue('anchor');
  const inclusionsRef = useRef<HTMLSpanElement>(null);

  const topicGroupIds = useMemo(() => (topicGroups ?? []).map((record) => record.id), [topicGroups]);

  const mainEntity = useMainEntity() as unknown as TopicGroup | TopicWithOwner;

  const { setModalOpen, setSelectedTopicGroupIds, setOriginalTopicGroupIds } = useEditTopicGroupContext();

  useEffect(() => {
    // Scrolls to topic inclusions section if the anchor is set.
    // On the topic group settings page, we have topic cards that include
    // links to this section
    if (anchor === 'topic-group-inclusions') {
      new Promise((resolve) => setTimeout(resolve, 100)).then(() => {
        // eslint-disable-next-line no-unused-expressions
        inclusionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [anchor]);

  return (
    <Flex flexDirection="column">
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <Text ref={inclusionsRef} variant="h4">
          Topic Group Inclusions
        </Text>
        {isCustomTopicOrTopicGroup(mainEntity.sharedWithEntityId) && (
          <>
            <SlButton
              disabled={isLoading}
              buttonSx={{
                paddingX: '12px'
              }}
              variant="contained"
              onClick={() => {
                setOriginalTopicGroupIds(topicGroupIds);
                setSelectedTopicGroupIds(topicGroupIds);
                setModalOpen(true);
              }}
            >
              Edit Inclusions
            </SlButton>
          </>
        )}
      </Flex>
      <Text
        variant="body1"
        sx={{
          marginBottom: '32px'
        }}
      >
        This topic is included in the following Topic Groups.
      </Text>
      <SlPaginator
        loading={isLoading}
        renderLoader={() => (
          <TopicGroupGrid>
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <GenericCardLoading width={250} height={416} key={index} />
            ))}
          </TopicGroupGrid>
        )}
        items={topicGroups}
        renderItems={(items: TopicCardRecord[]) =>
          items.length > 0 ? (
            <TopicGroupGrid>
              {items.map((topicGroup) => (
                <TopicGroupCard key={topicGroup.id} topicGroup={topicGroup} />
              ))}
            </TopicGroupGrid>
          ) : (
            <Flex justifyContent="center" marginTop="48px">
              <Flex maxWidth="244px">
                <NoDataPlaceHolderTableRow
                  icon={<SlTripleFolderIcon />}
                  title="Add topic groups"
                  description="There are currently no topic groups containing this topic."
                  link={
                    <SlClickThruAction
                      label="Add to topic group"
                      onClick={() => {
                        setSelectedTopicGroupIds([]);
                        setModalOpen(true);
                      }}
                    />
                  }
                />
              </Flex>
            </Flex>
          )
        }
        itemsPerPage={ITEMS_PER_PAGE}
      />
    </Flex>
  );
}
